// ./PublicPodcastScreens/EvacuationTimeTable.jsx
import React from "react";

function useIsMobile(breakpoint = 768) {
  const [isMobile, setIsMobile] = React.useState(
    typeof window !== "undefined" ? window.innerWidth <= breakpoint : false
  );
  React.useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= breakpoint);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [breakpoint]);
  return isMobile;
}

/**
 * Pure display-only table/cards.
 *
 * Props:
 * - rows?: Array<{ path?: string[]; exitName?: string; exit?: string; exit_name?: string;
 *                  leaveOnTime?: string; onTime?: string; on_time?: string;
 *                  leave10MinLate?: string; late10min?: string; late_10_min?: string; id?: string|number }>
 * - loading?: boolean
 * - error?: string
 * - nodeId?: string | number | null
 * - onRefresh?: () => void
 * - className?: string
 * - style?: React.CSSProperties
 */
export default function EvacuationTimeTable({
  rows = [],
  loading = false,
  error = "",
  nodeId = null,
  onRefresh,
  className,
  style
}) {
  const isMobile = useIsMobile(768);
  const hasRefresh = typeof onRefresh === "function";

  const emptyState = !nodeId ? (
    <div style={{ opacity: 0.8 }}>
      Select a starting location to see evacuation times.
    </div>
  ) : error ? (
    <div style={{ color: "#fca5a5" }}>{error}</div>
  ) : loading ? (
    <div style={{ opacity: 0.8 }}>Loading…</div>
  ) : rows.length === 0 ? (
    <div style={{ opacity: 0.8 }}>
      No saved evacuation times yet for this starting location.
    </div>
  ) : null;

  const containerStyle = {
    background: "rgba(15, 201, 238, 0.10)",
    border: "1px solid rgba(255,255,255,0.08)",
    borderRadius: 10,
    padding: 16,
    color: "#cbd5e1",
    ...style
  };

  return (
    <div className={className} style={containerStyle}>
      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "center",
          marginBottom: 12
        }}
      >
        {hasRefresh && (
          <button
            onClick={onRefresh}
            style={{
              appearance: "none",
              border: "1px solid rgba(255,255,255,0.18)",
              background: "transparent",
              color: "#e5e7eb",
              padding: "6px 10px",
              borderRadius: 8,
              cursor: loading ? "wait" : "pointer",
              fontWeight: 600
            }}
            disabled={loading}
            title={loading ? "Loading…" : "Refresh"}
          >
            {loading ? "Loading…" : "Refresh"}
          </button>
        )}
      </div>

      {emptyState ? (
        emptyState
      ) : isMobile ? (
        // ---------- MOBILE: stacked cards ----------
        <div style={{ display: "grid", gap: 12 }}>
          {rows.map((r, idx) => {
            const path = Array.isArray(r?.path) ? r.path : [];
            const routeDisplay = path.join(" \u2192 ");
            const exitName = r?.exitName || r?.exit || r?.exit_name || "";
            const onTime = r?.leaveOnTime || r?.onTime || r?.on_time || "—";
            const late10 =
              r?.leave10MinLate || r?.late10min || r?.late_10_min || "—";
            return (
              <div
                key={r?.id ?? idx}
                style={{
                  border: "1px solid rgba(255,255,255,0.10)",
                  borderRadius: 10,
                  padding: 12,
                  background: "rgba(255,255,255,0.02)"
                }}
              >
                <div style={{ fontSize: 12, opacity: 0.75, marginBottom: 4 }}>
                  Route
                </div>
                <div
                  style={{
                    fontWeight: 600,
                    wordBreak: "break-word",
                    overflowWrap: "anywhere",
                    lineHeight: 1.35,
                    marginBottom: 8
                  }}
                >
                  {routeDisplay || <span style={{ opacity: 0.8 }}>—</span>}
                </div>

                <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
                  <div>
                    <div
                      style={{ fontSize: 12, opacity: 0.75, marginBottom: 4 }}
                    >
                      Exit
                    </div>
                    <div>{exitName || <span style={{ opacity: 0.8 }}>—</span>}</div>
                  </div>

                  <div>
                    <div
                      style={{ fontSize: 12, opacity: 0.75, marginBottom: 4 }}
                    >
                      Times
                    </div>
                    <ul style={{ margin: 0, paddingLeft: 18 }}>
                      <li>Leave (on time): {onTime}</li>
                      <li style={{color: 'red'}}>Leave 10 min late: <b>{late10}</b></li>
                    </ul>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        // ---------- DESKTOP: table with 30% Route column and wrapping ----------
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed" }}>
            <colgroup>
              <col style={{ width: "40%" }} /> {/* Route column */}
              <col style={{ width: "20%" }} /> {/* Exit */}
              <col style={{ width: "40%" }} /> {/* Times */}
            </colgroup>
            <thead>
              <tr>
                <th
                  style={{
                    textAlign: "left",
                    padding: "10px 8px",
                    borderBottom: "1px solid rgba(255,255,255,0.12)"
                  }}
                >
                  Route
                </th>
                <th
                  style={{
                    textAlign: "left",
                    padding: "10px 8px",
                    borderBottom: "1px solid rgba(255,255,255,0.12)"
                  }}
                >
                  Exit
                </th>
                <th
                  style={{
                    textAlign: "left",
                    padding: "10px 8px",
                    borderBottom: "1px solid rgba(255,255,255,0.12)"
                  }}
                >
                  <b>Egress Times</b>
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, idx) => {
                const path = Array.isArray(r?.path) ? r.path : [];
                const routeDisplay = path.join(" \u2192 ");
                const exitName = r?.exitName || r?.exit || r?.exit_name || "";
                const onTime = r?.leaveOnTime || r?.onTime || r?.on_time || "—";
                const late10 =
                  r?.leave10MinLate || r?.late10min || r?.late_10_min || "—";

                return (
                  <tr key={r?.id ?? idx}>
                    <td
                      style={{
                        padding: "10px 8px",
                        borderBottom: "1px solid rgba(255,255,255,0.06)",
                        whiteSpace: "normal",
                        wordBreak: "break-word",
                        overflowWrap: "anywhere",
                        lineHeight: 1.35
                      }}
                    >
                      {routeDisplay || <span style={{ opacity: 0.8 }}>—</span>}
                    </td>
                    <td
                      style={{
                        padding: "10px 8px",
                        borderBottom: "1px solid rgba(255,255,255,0.06)"
                      }}
                    >
                      {exitName || <span style={{ opacity: 0.8 }}>—</span>}
                    </td>
                    <td
                      style={{
                        padding: "10px 8px",
                        borderBottom: "1px solid rgba(255,255,255,0.06)"
                      }}
                    >
                      <ul style={{ margin: 0, paddingLeft: 18 }}>
                        <li>Leave (on time): {onTime}</li>
                        <li style={{color: 'red'}}>10 min late: <b>{late10}</b></li>
                      </ul>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* footnote */}
      {rows.length > 0 && !loading && !error && nodeId && (
        <section
          aria-labelledby="disclaimer-title"
          style={{
            marginTop: 26,
            paddingTop: 10,
            textAlign: "left" // ensure not centered by any parent styles
          }}
        >
          <h3
            id="disclaimer-title"
            style={{ fontSize: 20, fontWeight: 800, margin: "0 0 10px 0" }}
          >
            Disclaimer
          </h3>

          <ul
            role="list"
            style={{
              listStyleType: "disc",
              listStylePosition: "outside",
              margin: 0,
              paddingLeft: "1.25rem",
              color: "#e5e7eb",
              lineHeight: 1.6
            }}
          >

            <li>WUI-Cast is for wildfire preparedness and education only.</li>
            <li>In an actual emergency, always follow official alerts, evacuation orders, road closures, and re-entry instructions.</li>
            <li>In California, evacuations are managed by authorized public safety agencies, including law enforcement, fire officials, emergency management, CHP, CAL FIRE, and local authorities.</li>
            <li>WUI-Go! simulations are planning estimates only and do not include real-time fire conditions, road closures, traffic control, or emergency field interventions.</li>
            <li>WUI-Go! and partners assume no responsibility for actions taken based on this information.</li>
          </ul>
        </section>
      )}
    </div>
  );
}
